import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calculator } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/shared/components/EmptyState';
import { Pagination } from '@/shared/components/Pagination';
import { PageShell } from '@/shared/components/page';
import { useSession } from '@/hooks/useSession';
import { useAccountManagers } from '@/features/crm/hooks/useCrmPipeline';
import { EstimatesTable } from '../components/EstimatesTable';
import { NativeSelect } from '../components/NativeSelect';
import { useEstimateList } from '../hooks/useEstimates';
import { isApprover } from '../lib/actions';
import { ESTIMATE_STATUSES, type EstimateStatus } from '../types';

const PAGE_SIZE = 25;

export function EstimatesListPage() {
  const { t } = useTranslation('crm');
  const { user } = useSession();
  const approver = isApprover(user?.postgres_roles ?? []);
  const [status, setStatus] = useState<EstimateStatus | ''>('');
  const [owner, setOwner] = useState('all'); // 'all', 'mine' or a PostgreSQL user id
  const [awaiting, setAwaiting] = useState(false);
  const [page, setPage] = useState(1);
  const { data: managers } = useAccountManagers();
  const ownerNames = useMemo(() => new Map((managers ?? []).map((m) => [m.id, m.name])), [managers]);

  const { data, isLoading, isError } = useEstimateList({
    status: status ? [status] : undefined,
    ownerUserId: owner === 'all' ? undefined : owner === 'mine' ? user?.pg_id : owner,
    awaitingApproval: approver && awaiting ? true : undefined,
    pageNumber: page - 1,
    pageLength: PAGE_SIZE,
  });
  const filtered = status !== '' || owner !== 'all' || awaiting;
  const clearFilters = () => {
    setStatus('');
    setOwner('all');
    setAwaiting(false);
    setPage(1);
  };

  return (
    <PageShell Icon={Calculator} subtitle={t('estimates.subtitle')}>
      <div className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-48 space-y-1">
            <Label htmlFor="estimate-status-filter">{t('estimates.filters.status')}</Label>
            <NativeSelect
              id="estimate-status-filter"
              value={status}
              onChange={(e) => { setStatus(e.target.value as EstimateStatus | ''); setPage(1); }}
            >
              <option value="">{t('estimates.filters.allStatuses')}</option>
              {ESTIMATE_STATUSES.map((s) => <option key={s} value={s}>{t(`estimates.status.${s}`)}</option>)}
            </NativeSelect>
          </div>
          <div className="w-56 space-y-1">
            <Label htmlFor="estimate-owner-filter">{t('estimates.filters.owner')}</Label>
            <NativeSelect id="estimate-owner-filter" value={owner} onChange={(e) => { setOwner(e.target.value); setPage(1); }}>
              <option value="all">{t('estimates.filters.everyone')}</option>
              <option value="mine">{t('estimates.filters.mine')}</option>
              {(managers ?? []).filter((m) => !m.isFormer && m.id !== user?.pg_id).map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </NativeSelect>
          </div>
          {approver && (
            <label className="flex h-9 items-center gap-2 text-sm">
              <input type="checkbox" className="h-4 w-4" checked={awaiting} onChange={(e) => { setAwaiting(e.target.checked); setPage(1); }} />
              {t('estimates.filters.awaitingApproval')}
            </label>
          )}
        </div>
        {isError ? (
          <Alert variant="destructive"><AlertDescription>{t('estimates.loadFailed')}</AlertDescription></Alert>
        ) : isLoading ? (
          <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
        ) : !data?.data.length ? (
          <EmptyState
            Icon={Calculator}
            heading={t('estimates.empty.heading')}
            description={t('estimates.empty.description')}
            isFiltered={filtered}
            onClearFilters={clearFilters}
          />
        ) : (
          <>
            <EstimatesTable rows={data.data} showContext ownerNames={ownerNames} />
            <Pagination page={page} pageSize={PAGE_SIZE} total={data.total} onPageChange={setPage} />
          </>
        )}
      </div>
    </PageShell>
  );
}
